import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Alert, Snackbar, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Add, Refresh } from '@mui/icons-material';
import rightCaretIcon from '../assets/icons/right-caret.svg';
import Footer from '../components/Footer';
import { blogApi, BlogArticle } from '../services/blogApi';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Load articles from API
  const loadArticles = async () => {
    try {
      setLoading(true);
      const response = await blogApi.getArticles({
        limit: 20,
        sort: 'date',
        order: 'desc'
      });
      setArticles(response.articles);
    } catch (error) {
      console.error('Error loading articles:', error);
      setSnackbar({
        open: true,
        message: 'Chyba při načítání článků',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate random article
  const handleGenerateArticle = async () => {
    try {
      setGenerating(true);
      setSnackbar({
        open: true,
        message: 'Generuji nový článek...',
        severity: 'info'
      });

      const result = await blogApi.generateRandomArticle();

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Článek "${result.article?.title}" byl úspěšně vygenerován!`,
          severity: 'success'
        });
        // Refresh article list
        await loadArticles();
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Nepodařilo se vygenerovat článek',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error generating article:', error);
      setSnackbar({
        open: true,
        message: 'Chyba při generování článku',
        severity: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  // Load articles on component mount
  useEffect(() => {
    loadArticles();
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{
      paddingTop: { xs: '84px', md: '120px' },
      minHeight: '100vh',
      backgroundColor: '#F2F2F2'
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: '1920px',
        margin: '0 auto',
        px: { xs: 2, sm: 3, md: 4, lg: 6 }
      }}>
      {/* Header Section */}
      <Box sx={{
        pt: { xs: '40px', md: '40px' },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '24px', md: '32px' },
        width: '100%',
        maxWidth: '1387px',
        mx: 'auto',
        mb: { xs: '40px', md: '60px' },
        position: 'relative',
        zIndex: 1
      }}>
        {/* Breadcrumb */}
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <Typography sx={{ 
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '11px',
            lineHeight: '14.3px',
            letterSpacing: '0.91%',
            textAlign: 'center',
            color: '#4C4C4C',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}
          onClick={() => navigate('/')}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/'); } }}
          >
            Domů
          </Typography>
          <Box 
            component="img" 
            src={rightCaretIcon} 
            alt="" 
            sx={{ 
              width: '10px', 
              height: '10px'
            }} 
            loading="lazy"
          />
          <Typography sx={{ 
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '11px',
            lineHeight: '14.3px',
            letterSpacing: '0.91%',
            textAlign: 'center',
            color: '#797979' 
          }}>
            Blog
          </Typography>
        </Box>

        {/* Title and Generate Button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 600,
            fontSize: { xs: '24px', sm: '28px', md: '36px' },
            lineHeight: { xs: '32px', sm: '36px', md: '44px' },
            letterSpacing: '2.78%',
            color: '#1F1F1F'
          }}>
            Právní blog
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={loadArticles}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                px: 2,
                color: '#1F1F1F',
                borderColor: '#1F1F1F',
                '&:hover': {
                  backgroundColor: 'rgba(31, 31, 31, 0.08)',
                  borderColor: '#1F1F1F'
                }
              }}
            >
              Obnovit
            </Button>

            <Button
              variant="contained"
              onClick={handleGenerateArticle}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : <Add />}
              sx={{
                bgcolor: '#1F1F1F !important',
                backgroundColor: '#1F1F1F !important',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#e72f0d !important',
                  backgroundColor: '#e72f0d !important'
                },
                '&:disabled': {
                  bgcolor: '#999999 !important',
                  backgroundColor: '#999999 !important'
                }
              }}
            >
              {generating ? 'Generuji...' : 'Generovat článek'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Loading State */}
      {loading && !articles.length && (
        <Box sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '1387px',
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: { xs: '20px', sm: '24px', md: '32px' },
          mb: { xs: '60px', md: '100px' }
        }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#FFFFFF', borderRadius: { xs: 2, md: 3 } }}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="70%" />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Skeleton variant="rounded" width={60} height={24} />
                <Skeleton variant="rounded" width={80} height={24} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Blog Posts Grid */}
      {!loading && articles.length > 0 && (
        <Box sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '1387px',
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: { xs: '20px', sm: '24px', md: '32px' },
          mb: { xs: '60px', md: '100px' },
          zIndex: 0
        }}>
          {articles.map((article) => (
            <Box
              key={article.id}
              onClick={() => navigate(`/blog/${article.slug || article.id}`)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/blog/${article.slug || article.id}`); } }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '12px', md: '16px' },
                padding: { xs: '16px', sm: '20px', md: '24px' },
                backgroundColor: '#FFFFFF',
                borderRadius: { xs: '16px', md: '20px' },
                boxShadow: '0px 2px 2px rgba(31, 31, 31, 0.15)',
                cursor: 'pointer',
                transition: 'all 0.5s ease-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 16px rgba(31, 31, 31, 0.15)'
                }
              }}
            >
            {/* Article Image */}
            <Box
              component="img"
              src={article.imageUrl}
              alt={article.title}
              sx={{
                width: '100%',
                height: { xs: '180px', sm: '200px', md: '220px' },
                objectFit: 'cover',
                borderRadius: { xs: '8px', md: '12px' },
                backgroundColor: '#F2F2F2'
              }}
              loading="lazy"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'flex';
                target.style.alignItems = 'center';
                target.style.justifyContent = 'center';
                target.alt = 'Obrázek článku';
              }}
            />

            {/* Date and AI Badge */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '18px',
                color: '#797979'
              }}>
                {new Date(article.date).toLocaleDateString('cs-CZ')}
              </Typography>
              {article.aiGenerated && (
                <Typography sx={{
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: '10px',
                  backgroundColor: '#E3F2FD',
                  color: '#1976D2',
                  px: 1,
                  py: 0.5,
                  borderRadius: '12px'
                }}>
                  AI
                </Typography>
              )}
            </Box>

            {/* Title */}
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: { xs: '16px', sm: '18px', md: '20px' },
              lineHeight: { xs: '22px', sm: '25px', md: '28px' },
              color: '#1F1F1F'
            }}>
              {article.title}
            </Typography>

            {/* Description */}
            <Typography sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: { xs: '13px', md: '14px' },
              lineHeight: { xs: '18px', md: '20px' },
              color: '#666666'
            }}>
              {article.description}
            </Typography>

            {/* Tags */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {article.tags.slice(0, 3).map((tag, index) => (
                <Typography
                  key={index}
                  sx={{
                    fontFamily: 'Inter',
                    fontSize: '11px',
                    backgroundColor: '#F5F5F5',
                    color: '#666666',
                    px: 1,
                    py: 0.5,
                    borderRadius: '8px'
                  }}
                >
                  {tag}
                </Typography>
              ))}
            </Box>

            {/* Read More Link */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: '8px'
            }}>
              <Typography sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '13px', md: '14px' },
                lineHeight: '20px',
                color: '#1F1F1F'
              }}>
                Číst více
              </Typography>
              <Box 
                component="img" 
                src={rightCaretIcon} 
                alt=">" 
                sx={{ 
                  width: { xs: '10px', md: '12px' }, 
                  height: { xs: '10px', md: '12px' }
                }} 
              />
            </Box>
          </Box>
          ))}
        </Box>
      )}

      {/* Empty State */}
      {!loading && articles.length === 0 && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          textAlign: 'center'
        }}>
          <Typography sx={{
            fontFamily: 'Inter',
            fontSize: '18px',
            color: '#666666',
            mb: 2
          }}>
            Zatím nejsou k dispozici žádné články.
          </Typography>
          <Button
            variant="contained"
            onClick={handleGenerateArticle}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} /> : <Add />}
            sx={{
              bgcolor: '#1F1F1F !important',
              backgroundColor: '#1F1F1F !important',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: '#e72f0d !important',
                backgroundColor: '#e72f0d !important'
              },
              '&:disabled': {
                bgcolor: '#999999 !important',
                backgroundColor: '#999999 !important'
              }
            }}
          >
            {generating ? 'Generuji...' : 'Vytvořit první článek'}
          </Button>
        </Box>
      )}

      {/* Load More Button - only show if there are articles */}
      {articles.length > 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: { xs: '60px', md: '100px' }
        }}>
          <Button
            onClick={loadArticles}
            disabled={loading}
            sx={{
              padding: { xs: '10px 16px', md: '12px 20px' },
              bgcolor: '#1F1F1F !important',
              backgroundColor: '#1F1F1F !important',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: '#e72f0d !important',
                backgroundColor: '#e72f0d !important'
              },
              '&:disabled': {
                bgcolor: '#999999 !important',
                backgroundColor: '#999999 !important'
              }
            }}
          >
            <Typography sx={{
              fontFamily: 'Inter',
              fontSize: { xs: '13px', md: '14px' },
              fontWeight: 500,
              lineHeight: '20px',
              color: '#FFFFFF'
            }}>
              {loading ? 'Načítám...' : 'Obnovit články'}
            </Typography>
          </Button>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} role="alert" aria-live="polite" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Footer */}
      <Footer />
      </Box>
    </Box>
  );
};

export default BlogPage;
